import type { Translations } from '@/i18n/locales/en';

/**
 * Payment status in operator language. An online (card) payment that is still
 * PROCESSING reads as "Awaiting card payment" rather than the generic
 * "Processing" - the owner sees what's actually happening, never provider internals.
 * `labels` is the current-locale `t.orders.paymentStatusLabels` map.
 *
 * This is the detail-inspector status TEXT. It is deliberately distinct from
 * `paymentPill` (payment-pill.ts), which maps the same status onto the list-row
 * attention pill from a different wording set (`t.orders.pill`): "Paid" /
 * "Awaiting" / "Failed" / "Refund needed" rather than the literal status name.
 */
export function paymentStatusLabel(
  methodKind: string,
  status: string,
  labels: Translations['orders']['paymentStatusLabels'],
): string {
  if (methodKind === 'ONLINE' && status === 'PROCESSING') return labels.awaitingCardPayment;
  // Captured but held by the provider for review (PayPal PENDING / eCheck) - the
  // owner must NOT prepare it yet; the order confirms itself once the funds clear.
  if (methodKind === 'ONLINE' && status === 'ON_HOLD') return labels.underReviewByProvider;
  // Card-at-handoff is collected in person on the owner's POS terminal (settled
  // like cash), so a pending payment reads as "awaiting collection at handoff".
  if (methodKind === 'CARD_ON_HANDOFF' && status === 'PENDING_COLLECTION')
    return labels.awaitingCardCollection;
  return (labels as Record<string, string>)[status] ?? status;
}
